import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  HeartIcon
} from '@heroicons/react/24/outline'
import CustomLink from '@/components/common/CustomLink'

// Types
import type { FC } from 'react'

const Footer: FC = () => {
  const currentYear = new Date().getFullYear()
  const navigate = useNavigate()
  const location = useLocation()

  // Handle navigation with force reload for same page
  const handleNavigation = (href: string, e: React.MouseEvent<HTMLAnchorElement>) => {
    const currentPath = location.pathname + location.search
    const targetPath = href

    // If clicking on the same page, force navigation
    if (currentPath !== targetPath && location.pathname === new URL(href, window.location.origin).pathname) {
      e.preventDefault()
      navigate(href, { replace: false })
      // Force scroll to top
      window.scrollTo(0, 0)
    }
  }

  const footerLinks = {
    company: [
      { name: 'เกี่ยวกับเรา', href: '/about' },
      { name: 'ติดต่อเรา', href: '/contact' },
      { name: 'ข้อมูลความเป็นส่วนตัว', href: '/privacy' },
      { name: 'ข้อกำหนดการใช้งาน', href: '/terms' },
    ],
    services: [
      { name: 'ค้นหาบริษัท', href: '/search' },
      { name: 'บริษัทแนะนำ', href: '/search?featured=true' },
      { name: 'ประเภทบริการ', href: '/search?showFilters=true&focusFilter=serviceType' },
      { name: 'สมัครสมาชิก', href: '/register' },
    ],
    support: [
      { name: 'ช่วยเหลือ', href: '/help' },
      { name: 'คำถามที่พบบ่อย', href: '/faq' },
      { name: 'รายงานปัญหา', href: '/report' },
      { name: 'แนะนำเว็บไซต์', href: '/feedback' },
    ]
  }

  return (
    <footer className="bg-neutral-900 text-white">
      {/* Main Footer Content */}
      <div className="container-wrapper py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">T</span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-xl text-white leading-none">
                  ไทยเซ็กคิวริตี้
                </span>
                <span className="text-sm text-neutral-400 leading-none">
                  ไดเรกทอรี่
                </span>
              </div>
            </div>

            <p className="text-neutral-300 text-sm mb-6 leading-relaxed">
              แหล่งรวมข้อมูลบริษัทรักษาความปลอดภัยที่เชื่อถือได้
              ในประเทศไทย พร้อมข้อมูลติดต่อครบถ้วน
            </p>

            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-sm text-neutral-300">
                <MapPinIcon className="w-4 h-4 text-primary-400 flex-shrink-0" />
                <span>นนทบุรี ประเทศไทย</span>
              </div>
              <div className="flex items-center space-x-3 text-sm text-neutral-300">
                <PhoneIcon className="w-4 h-4 text-primary-400 flex-shrink-0" />
                <span>02-821-5799</span>
              </div>
              <div className="flex items-center space-x-3 text-sm text-neutral-300">
                <EnvelopeIcon className="w-4 h-4 text-primary-400 flex-shrink-0" />
                <span>info@koder3.com</span>
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">บริษัท</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <CustomLink
                    to={link.href}
                    className="text-neutral-300 hover:text-white text-sm transition-colors duration-200"
                  >
                    {link.name}
                  </CustomLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">บริการ</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <CustomLink
                    to={link.href}
                    onClick={(e) => handleNavigation(link.href, e)}
                    className="text-neutral-300 hover:text-white text-sm transition-colors duration-200"
                  >
                    {link.name}
                  </CustomLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">ช่วยเหลือ</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <CustomLink
                    to={link.href}
                    className="text-neutral-300 hover:text-white text-sm transition-colors duration-200"
                  >
                    {link.name}
                  </CustomLink>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-neutral-800">
        <div className="container-wrapper py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-1 text-sm text-neutral-400">
              <span>© {currentYear} ไทยเซ็กคิวริตี้ไดเรกทอรี่ |</span>
              <span>สร้างด้วย</span>
              <HeartIcon className="w-4 h-4 text-red-500" />
              <span>ในประเทศไทย</span>
            </div>

            <div className="flex items-center space-x-6">
              <CustomLink
                to="/privacy"
                className="text-sm text-neutral-400 hover:text-white transition-colors duration-200"
              >
                นโยบายความเป็นส่วนตัว
              </CustomLink>
              <CustomLink
                to="/terms"
                className="text-sm text-neutral-400 hover:text-white transition-colors duration-200"
              >
                ข้อกำหนดการใช้งาน
              </CustomLink>
              <CustomLink
                to="/sitemap"
                className="text-sm text-neutral-400 hover:text-white transition-colors duration-200"
              >
                Sitemap
              </CustomLink>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer